import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Anchor, ArrowRight, Sparkles, Globe, Megaphone, Palette, ShoppingBag,
  Layers, CheckCircle2, Mail, Star, Users, ShieldCheck, Award, ChevronRight, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const SUPPORT_EMAIL = 'sailservicesa@gmail.com';

const services = [
  {
    icon: <Layers className="w-6 h-6" />,
    title: 'SAIL Books — SaaS CRM & Bookkeeping',
    desc: 'Our flagship product. A full CRM, invoicing, bookkeeping, and SARS-ready compliance suite — built for South African SMEs.',
    cta: 'See the product',
    href: '/pricing',
    featured: true,
  },
  {
    icon: <Globe className="w-6 h-6" />,
    title: 'Website Creation',
    desc: 'Beautiful, fast websites that turn visitors into customers. From single-page portfolios to full e-commerce stores.',
    cta: 'Book a free consultation',
    href: '/consultation?service=website',
  },
  {
    icon: <Megaphone className="w-6 h-6" />,
    title: 'Marketing Solutions',
    desc: 'Social media campaigns, email marketing, paid ads — strategies that get your brand noticed and your products sold.',
    cta: 'Book a free consultation',
    href: '/consultation?service=marketing',
  },
  {
    icon: <Palette className="w-6 h-6" />,
    title: 'Brand Identity Creation',
    desc: 'Logos, colour palettes, brand guidelines and packaging. We help you look, feel and speak like the brand you want to be.',
    cta: 'Book a free consultation',
    href: '/consultation?service=branding',
  },
];

const websiteExamples = [
  {
    title: 'Artisan E-commerce',
    tag: 'For makers & craftspeople',
    img: 'https://images.unsplash.com/photo-1523398845774-0d176ba6fd41?w=1200&q=80',
    desc: 'Beautiful product galleries, secure checkout, and a story-led homepage that lets your craft speak.'
  },
  {
    title: 'Service Business',
    tag: 'Consulting, agencies, studios',
    img: 'https://images.unsplash.com/photo-1566476927456-446189d7b1ca?w=1200&q=80',
    desc: 'Lead-capture forms, booking flows, and a portfolio that turns first-time visitors into long-term clients.'
  },
  {
    title: 'Portfolio & Personal Brand',
    tag: 'Photographers, designers, coaches',
    img: 'https://images.pexels.com/photos/190364/pexels-photo-190364.jpeg?w=1200&q=80',
    desc: 'A clean, fast portfolio that puts your work first — with the right hooks to convert visitors into bookings.'
  },
  {
    title: 'Brand Showcase',
    tag: 'Established companies',
    img: 'https://images.unsplash.com/photo-1426024120108-99cc76989c71?w=1200&q=80',
    desc: 'Multi-page brand sites with newsroom, careers, and integrations — built to scale with your team.'
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <div className="landing-root">
      <NavBar onContact={() => setContactOpen(true)} />
      <Hero onContact={() => setContactOpen(true)} />
      <ProductFeature />
      <ServicesGrid />
      <ArtisansSection />
      <WebsiteExamples />
      <PricingTeaser />
      <ConsultationCTA />
      <Footer onContact={() => setContactOpen(true)} />
      {contactOpen && <ContactModal onClose={() => setContactOpen(false)} />}
    </div>
  );
}

function NavBar({ onContact }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? 'backdrop-blur-xl bg-white/70 shadow-[0_4px_30px_rgba(13,59,102,0.08)] border-b border-[var(--border-glass)]' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-deep)] flex items-center justify-center shadow-lg shadow-[var(--ocean-surface)]/30">
            <Anchor className="w-5 h-5 text-white" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-bold tracking-tight text-lg text-[var(--ocean-deep)]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>SAIL</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">Services SA</span>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[var(--text-secondary)]">
          <a href="#product" className="hover:text-[var(--ocean-surface)] transition">Product</a>
          <a href="#services" className="hover:text-[var(--ocean-surface)] transition">Services</a>
          <a href="#artisans" className="hover:text-[var(--ocean-surface)] transition">For Artisans</a>
          <a href="#examples" className="hover:text-[var(--ocean-surface)] transition">Examples</a>
          <Link to="/pricing" className="hover:text-[var(--ocean-surface)] transition">Pricing</Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="hidden sm:inline-flex text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--ocean-deep)] px-3 py-2 rounded-lg transition">Sign in</Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-mid)] text-white text-sm font-semibold shadow-lg shadow-[var(--ocean-surface)]/25 hover:shadow-[var(--ocean-surface)]/40 hover:-translate-y-0.5 transition-all"
          >
            Start free trial <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero({ onContact }) {
  return (
    <section className="relative pt-32 pb-24 md:pt-44 md:pb-32 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-1/3 w-[700px] h-[700px] rounded-full bg-[var(--ocean-shallow)] opacity-20 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-[var(--ocean-seaweed)] opacity-15 blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur-md border border-[var(--border-glass)] text-xs font-semibold text-[var(--ocean-deep)] mb-6 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-[var(--ocean-surface)]" />
            SAIL Services SA — Brand identity, marketing & SaaS for SMEs
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-[var(--ocean-deep)] leading-[1.02]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            We give brands an identity — <span className="bg-gradient-to-r from-[var(--ocean-surface)] via-[var(--ocean-seaweed)] to-[var(--ocean-shallow)] bg-clip-text text-transparent">and a business that runs itself.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-[var(--text-secondary)] max-w-2xl leading-relaxed">
            From bold brand identities and beautiful websites, to <strong>SAIL Books</strong> — our SaaS CRM & bookkeeping platform built for South African SMEs. We work with all brands, with a special focus on lifting up local <strong>artisans &amp; makers</strong>.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Hi%20SAIL%20-%20I'd%20like%20to%20chat`} className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-deep)] text-white font-semibold shadow-xl shadow-[var(--ocean-surface)]/30 hover:shadow-[var(--ocean-surface)]/50 hover:-translate-y-0.5 transition-all">
              <Mail className="w-5 h-5" /> Send us an email
            </a>
            <Link to="/consultation" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-white/80 backdrop-blur-md border border-[var(--border-strong)] text-[var(--ocean-deep)] font-semibold hover:bg-white hover:-translate-y-0.5 transition-all">
              Book free consultation <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
            <Stat value="14d" label="Free trial" />
            <Stat value="1 hr" label="Free consult" />
            <Stat value="SA" label="Built local" />
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl shadow-[var(--ocean-deep)]/20 border border-white/40">
            <img src="https://images.unsplash.com/photo-1698026496470-c173b40e64ae?w=900&q=80" alt="Abstract ocean" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--ocean-deep)]/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 p-5 rounded-2xl bg-white/85 backdrop-blur-xl border border-white/60 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[var(--ocean-surface)]/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[var(--ocean-surface)]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[var(--ocean-deep)]">Brand. Website. Books.</p>
                  <p className="text-xs text-[var(--text-muted)]">One partner. One sail.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="text-3xl font-extrabold text-[var(--ocean-deep)]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>{value}</p>
      <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mt-1">{label}</p>
    </div>
  );
}

function ProductFeature() {
  return (
    <section id="product" className="py-20 md:py-28 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="rounded-[2rem] overflow-hidden bg-gradient-to-br from-[var(--ocean-deep)] via-[var(--ocean-mid)] to-[var(--ocean-surface)] p-1 shadow-2xl shadow-[var(--ocean-deep)]/30">
          <div className="rounded-[1.9rem] bg-gradient-to-br from-[var(--ocean-deep)] to-[var(--ocean-mid)] p-10 md:p-16 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-semibold text-white mb-5">
                <Layers className="w-3.5 h-3.5" /> Our SaaS Product
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-white leading-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
                SAIL Books — your CRM, invoicing & bookkeeping in one place.
              </h2>
              <p className="mt-5 text-lg text-white/80 leading-relaxed">
                Built for South African small businesses. Manage clients, send compliant tax invoices, reconcile bank statements, draft documents with AI, and stay on top of SARS deadlines — all from one beautiful dashboard.
              </p>
              <ul className="mt-7 space-y-3">
                {['CRM, invoices & POPs that look professional','AI Assist (Claude Sonnet 4.5) for drafts & categorisation','Bank statement reconciliation & anomaly detection','SARS compliance reminders & document library'].map(f => (
                  <li key={f} className="flex items-start gap-3 text-white/90">
                    <CheckCircle2 className="w-5 h-5 text-[var(--ocean-shallow)] flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/register" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white text-[var(--ocean-deep)] font-semibold hover:bg-[var(--ocean-foam)] transition-all">
                  Start 14-day free trial <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/pricing" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white/10 border border-white/30 text-white font-semibold hover:bg-white/20 transition-all">
                  See pricing
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-5 shadow-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-3 h-3 rounded-full bg-red-400"></span>
                  <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                  <span className="w-3 h-3 rounded-full bg-green-400"></span>
                  <span className="text-xs text-white/60 ml-2">books.sail.com / dashboard</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {['Revenue MTD','Outstanding','VAT Due'].map((l, i) => (
                    <div key={l} className="p-3 rounded-xl bg-white/10 border border-white/10">
                      <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">{l}</p>
                      <p className="text-xl font-bold text-white mt-1">R {[42500, 18900, 7320][i].toLocaleString()}</p>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  {[
                    {n:'Karabo Designs', a:'R 8,500', s:'PAID'},
                    {n:'Cape Coast Pottery', a:'R 3,200', s:'DUE'},
                    {n:'Zulu Threads', a:'R 12,400', s:'PAID'},
                  ].map(r => (
                    <div key={r.n} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                      <span className="text-sm text-white/90">{r.n}</span>
                      <span className="text-sm font-semibold text-white">{r.a}</span>
                      <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${r.s==='PAID'?'bg-[var(--ocean-seaweed)]/30 text-white':'bg-amber-400/30 text-amber-100'}`}>{r.s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesGrid() {
  return (
    <section id="services" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[var(--ocean-deep)]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            One partner. Every part of your brand.
          </h2>
          <p className="mt-4 text-lg text-[var(--text-secondary)] max-w-2xl mx-auto">
            Beyond SAIL Books, we help you build the identity that lives on every customer touchpoint — website, marketing, packaging and more.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {services.map(s => (
            <div key={s.title} className={`relative rounded-3xl p-8 transition-all hover:-translate-y-1 border ${s.featured ? 'bg-gradient-to-br from-[var(--ocean-deep)] to-[var(--ocean-surface)] border-transparent text-white shadow-xl shadow-[var(--ocean-surface)]/20' : 'bg-white/70 backdrop-blur-xl border-[var(--border-glass)] hover:shadow-xl hover:shadow-[var(--ocean-surface)]/10'}`}>
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${s.featured ? 'bg-white/15 text-white' : 'bg-[var(--ocean-surface)]/10 text-[var(--ocean-surface)]'}`}>
                {s.icon}
              </div>
              <h3 className={`text-2xl font-bold mb-3 ${s.featured ? 'text-white' : 'text-[var(--ocean-deep)]'}`} style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>{s.title}</h3>
              <p className={`leading-relaxed ${s.featured ? 'text-white/80' : 'text-[var(--text-secondary)]'}`}>{s.desc}</p>
              <Link to={s.href} className={`mt-6 inline-flex items-center gap-1.5 text-sm font-semibold ${s.featured ? 'text-white' : 'text-[var(--ocean-surface)] hover:text-[var(--ocean-deep)]'}`}>
                {s.cta} <ChevronRight className="w-4 h-4" />
              </Link>
              {s.featured && (
                <div className="absolute top-6 right-6 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-[10px] font-bold uppercase tracking-wider text-white">SaaS</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ArtisansSection() {
  return (
    <section id="artisans" className="py-20 md:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-[var(--ocean-seaweed)]/20 blur-2xl" />
            <div className="grid grid-cols-2 gap-4">
              <img src="https://images.pexels.com/photos/35117997/pexels-photo-35117997.jpeg?w=600&q=80" alt="Traditional fabric dyeing artisan" className="rounded-3xl object-cover h-80 w-full shadow-xl border border-white/40" />
              <img src="https://images.pexels.com/photos/32409583/pexels-photo-32409583.jpeg?w=600&q=80" alt="African pottery artisan" className="rounded-3xl object-cover h-80 w-full shadow-xl border border-white/40 mt-10" />
            </div>
          </div>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--ocean-coral)]/10 border border-[var(--ocean-coral)]/30 text-xs font-bold text-[var(--ocean-coral)] mb-5">
              <Award className="w-3.5 h-3.5" /> Our heart
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[var(--ocean-deep)] leading-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
              Built for all brands. <span className="text-[var(--ocean-surface)]">Made for artisans.</span>
            </h2>
            <p className="mt-5 text-lg text-[var(--text-secondary)] leading-relaxed">
              South Africa's artisans, makers, weavers, potters, and craftspeople are the soul of our local economy. We've made it our mission to help them tell their stories online — and to handle the books while they handle the craft.
            </p>
            <ul className="mt-7 space-y-4">
              {[
                { i: <ShoppingBag className="w-5 h-5" />, t: 'Storefronts that sell', d: 'Beautiful product pages that show off your craft and convert browsers to buyers.' },
                { i: <Megaphone className="w-5 h-5" />, t: 'Marketing that respects your story', d: 'Campaigns that lead with your heritage and craftsmanship, not just discount codes.' },
                { i: <Layers className="w-5 h-5" />, t: 'Books done for you', d: 'Use SAIL Books to send invoices, track sales, and stay SARS-ready without an accountant.' },
              ].map(x => (
                <li key={x.t} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[var(--ocean-surface)]/10 text-[var(--ocean-surface)] flex items-center justify-center flex-shrink-0">{x.i}</div>
                  <div>
                    <p className="font-bold text-[var(--ocean-deep)]">{x.t}</p>
                    <p className="text-sm text-[var(--text-secondary)] mt-1">{x.d}</p>
                  </div>
                </li>
              ))}
            </ul>
            <Link to="/consultation?service=branding" className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-br from-[var(--ocean-coral)] to-[#e85555] text-white font-semibold shadow-lg shadow-[var(--ocean-coral)]/30 hover:-translate-y-0.5 transition-all">
              Book your free artisan consultation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function WebsiteExamples() {
  return (
    <section id="examples" className="py-20 md:py-28 bg-gradient-to-b from-transparent to-[var(--ocean-foam)]/40">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur-md border border-[var(--border-glass)] text-xs font-semibold text-[var(--ocean-deep)] mb-4">
            <Globe className="w-3.5 h-3.5 text-[var(--ocean-surface)]" /> Websites we build
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[var(--ocean-deep)]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            Examples of what we can create for you.
          </h2>
          <p className="mt-4 text-lg text-[var(--text-secondary)] max-w-2xl mx-auto">
            A taste of the kinds of websites our team can craft — fully custom, fully yours.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {websiteExamples.map(w => (
            <div key={w.title} className="group relative rounded-3xl overflow-hidden bg-white/70 backdrop-blur-xl border border-[var(--border-glass)] hover:shadow-2xl hover:shadow-[var(--ocean-surface)]/15 transition-all">
              <div className="aspect-[16/10] overflow-hidden bg-[var(--ocean-foam)]">
                <img src={w.img} alt={w.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-7">
                <p className="text-xs uppercase tracking-wider font-bold text-[var(--ocean-surface)] mb-1">{w.tag}</p>
                <h3 className="text-2xl font-bold text-[var(--ocean-deep)]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>{w.title}</h3>
                <p className="mt-2 text-[var(--text-secondary)]">{w.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/consultation?service=website" className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-mid)] text-white font-semibold shadow-xl shadow-[var(--ocean-surface)]/30 hover:-translate-y-0.5 transition-all">
            Book a free website consultation <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function PricingTeaser() {
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6">
        <div className="rounded-[2rem] bg-white/70 backdrop-blur-xl border border-[var(--border-glass)] p-10 md:p-16 grid md:grid-cols-2 gap-10 items-center shadow-xl shadow-[var(--ocean-surface)]/10">
          <div>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[var(--ocean-deep)] leading-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
              Try SAIL Books free for 14 days.
            </h2>
            <p className="mt-4 text-lg text-[var(--text-secondary)]">
              No credit card required. After your trial, choose the plan that fits — from Free for solopreneurs to Enterprise for teams up to 10 users.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-deep)] text-white font-semibold shadow-lg shadow-[var(--ocean-surface)]/30 hover:-translate-y-0.5 transition-all">
                Start 14-day free trial
              </Link>
              <Link to="/pricing" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white border border-[var(--border-strong)] text-[var(--ocean-deep)] font-semibold hover:-translate-y-0.5 transition-all">
                Compare plans
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              {n:'Free', p:'R0'},
              {n:'Starter', p:'R950'},
              {n:'Pro', p:'R1,800', hl:true},
              {n:'Enterprise', p:'R3,500'},
            ].map(t => (
              <div key={t.n} className={`p-5 rounded-2xl border ${t.hl ? 'bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-deep)] text-white border-transparent shadow-lg' : 'bg-white border-[var(--border-glass)]'}`}>
                <p className={`text-xs font-bold uppercase tracking-wider ${t.hl ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>{t.n}</p>
                <p className={`text-3xl font-extrabold mt-2 ${t.hl ? 'text-white' : 'text-[var(--ocean-deep)]'}`} style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>{t.p}</p>
                <p className={`text-xs mt-1 ${t.hl ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>per user / month</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTA() {
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-5xl mx-auto px-6">
        <div className="rounded-[2rem] bg-gradient-to-br from-[var(--ocean-deep)] via-[var(--ocean-mid)] to-[var(--ocean-surface)] p-10 md:p-16 text-center shadow-2xl shadow-[var(--ocean-deep)]/30 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-[var(--ocean-seaweed)]/30 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-bold text-white mb-5">
              <Star className="w-3.5 h-3.5" /> Your first consultation is on us
            </div>
            <h2 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight leading-[1.05]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
              Let's build the brand<br/>the world remembers.
            </h2>
            <p className="mt-5 text-lg text-white/80 max-w-2xl mx-auto">
              Book a no-strings, one-hour consultation. We'll listen to your story, audit where you are, and map out where you could go — completely free.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/consultation" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-white text-[var(--ocean-deep)] font-bold hover:-translate-y-0.5 transition-all shadow-xl">
                Book free consultation <ArrowRight className="w-5 h-5" />
              </Link>
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Hi%20SAIL`} className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-white/10 border border-white/30 text-white font-bold hover:bg-white/20 transition-all">
                <Mail className="w-5 h-5" /> Email us instead
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ onContact }) {
  return (
    <footer className="border-t border-[var(--border-glass)] bg-white/40 backdrop-blur-xl mt-10">
      <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-deep)] flex items-center justify-center">
              <Anchor className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-[var(--ocean-deep)]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>SAIL Services SA</p>
              <p className="text-xs text-[var(--text-muted)]">Brand identity. Marketing. SaaS.</p>
            </div>
          </div>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">South African brand-identity and SaaS partner for SMEs and artisans.</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider font-bold text-[var(--text-muted)] mb-3">Product</p>
          <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
            <li><Link to="/pricing" className="hover:text-[var(--ocean-surface)]">Pricing</Link></li>
            <li><Link to="/register" className="hover:text-[var(--ocean-surface)]">Start free trial</Link></li>
            <li><Link to="/login" className="hover:text-[var(--ocean-surface)]">Sign in</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider font-bold text-[var(--text-muted)] mb-3">Services</p>
          <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
            <li><Link to="/consultation?service=website" className="hover:text-[var(--ocean-surface)]">Website Creation</Link></li>
            <li><Link to="/consultation?service=marketing" className="hover:text-[var(--ocean-surface)]">Marketing</Link></li>
            <li><Link to="/consultation?service=branding" className="hover:text-[var(--ocean-surface)]">Brand Identity</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider font-bold text-[var(--text-muted)] mb-3">Contact</p>
          <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
            <li><a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-[var(--ocean-surface)] flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {SUPPORT_EMAIL}</a></li>
            <li><button onClick={onContact} className="hover:text-[var(--ocean-surface)]">Send us a message</button></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[var(--border-glass)] py-5 text-center text-xs text-[var(--text-muted)]">
        &copy; {new Date().getFullYear()} SAIL Services SA. All rights reserved.
      </div>
    </footer>
  );
}

export function FloatingEmail() {
  return (
    <Link
      to="/consultation"
      data-testid="floating-email-cta"
      className="fixed bottom-24 right-6 z-30 group inline-flex items-center gap-2 px-5 py-4 rounded-full bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-deep)] text-white font-semibold shadow-2xl shadow-[var(--ocean-deep)]/40 hover:scale-105 hover:shadow-[var(--ocean-surface)]/50 transition-all"
      title="Get in touch — first consult is free"
    >
      <Mail className="w-5 h-5" />
      <span className="hidden sm:inline">Email us</span>
      <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[var(--ocean-coral)] animate-ping" />
      <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[var(--ocean-coral)]" />
    </Link>
  );
}

function ContactModal({ onClose }) {
  const [form, setForm] = useState({ name:'', email:'', subject:'', message:'' });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact/send`, form);
      toast.success("Thanks! We'll be in touch soon.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
    } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[var(--ocean-deep)]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-2xl p-7 relative" onClick={e=>e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-lg hover:bg-[var(--ocean-foam)] flex items-center justify-center"><X className="w-4 h-4"/></button>
        <h3 className="text-2xl font-bold text-[var(--ocean-deep)]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Send us a message</h3>
        <p className="text-sm text-[var(--text-secondary)] mt-1">We reply within one working day.</p>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input required placeholder="Your name" className="w-full px-4 py-3 rounded-xl border border-[var(--border-glass)] bg-white focus:outline-none focus:border-[var(--ocean-surface)]" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} />
          <input required type="email" placeholder="Email" className="w-full px-4 py-3 rounded-xl border border-[var(--border-glass)] bg-white focus:outline-none focus:border-[var(--ocean-surface)]" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} />
          <input placeholder="Subject" className="w-full px-4 py-3 rounded-xl border border-[var(--border-glass)] bg-white focus:outline-none focus:border-[var(--ocean-surface)]" value={form.subject} onChange={e=>setForm({...form, subject:e.target.value})} />
          <textarea required rows={4} placeholder="How can we help?" className="w-full px-4 py-3 rounded-xl border border-[var(--border-glass)] bg-white focus:outline-none focus:border-[var(--ocean-surface)] resize-none" value={form.message} onChange={e=>setForm({...form, message:e.target.value})} />
          <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-deep)] text-white font-semibold disabled:opacity-50">
            {submitting ? 'Sending…' : 'Send message'}
          </button>
        </form>
      </div>
    </div>
  );
}
